import React, { useEffect, useRef, useState } from "react";
import { getList } from "../../api/generics";
import Document from "../../models/document";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./DocumentSpreadsheetCrud.css";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import DocumentSpreadsheetGrid from "./document-spreadsheet-crud/DocumentSpreadsheetGrid";
import PdfPreviewModal from "./PdfPreviewModal";
import VersionsModal from "./document-spreadsheet-crud/VersionsModal";
import {
  getSpreadsheetRowClass,
  openNativeDatePicker,
  setGridDateValue,
  stopGridMouseEvent,
} from "./document-spreadsheet-crud/documentSpreadsheetUtils";
import {
  formatDateForInput,
  isExpired,
} from "./document-spreadsheet-crud/dateUtils";
import useChangedCells from "./document-spreadsheet-crud/useChangedCells";
import useDocumentSpreadsheetColumns from "./document-spreadsheet-crud/useDocumentSpreadsheetColumns";
import useDocumentDownload from "./useDocumentDownload";
import useDocumentPersistence from "./document-spreadsheet-crud/useDocumentPersistence";
import useDocumentRowActions from "./document-spreadsheet-crud/useDocumentRowActions";
import useDocumentVersions from "./document-spreadsheet-crud/useDocumentVersions";
import usePdfPreview from "./usePdfPreview";
import useSpreadsheetReferenceData from "./document-spreadsheet-crud/useSpreadsheetReferenceData";

const SORT_MAP: Record<string, Record<string, string>> = {
  name: { asc: "name", desc: "-name" },
  expirationDate: { asc: "expiration_date", desc: "-expiration_date" },
  createdAt: { asc: "created_at", desc: "-created_at" },
};

interface BackendParams {
  ordering?: string;
  filter_specs?: string;
}

const DocumentSpreadsheetCrud = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [backendParams, setBackendParams] = useState<BackendParams>({});
  const [gridApi, setGridApi] = useState<any>(null);

  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id,
  );

  // Refs so the stable datasource closure reads current values without re-creating
  const backendParamsRef = useRef(backendParams);
  const companyIdRef = useRef(companyId);
  const documentsRef = useRef(documents);
  const assetsRef = useRef<any[]>([]);

  useEffect(() => {
    backendParamsRef.current = backendParams;
  }, [backendParams]);
  useEffect(() => {
    companyIdRef.current = companyId;
  }, [companyId]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const {
    assets,
    documentCategories,
    documentSubcategories,
    fetchReferenceData,
    subcompanies,
    tags,
  } = useSpreadsheetReferenceData(companyId);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  const { downloadingIds, handleDownloadClick } = useDocumentDownload();
  const {
    closePdfPreview,
    handlePreviewPdfClick,
    isPdfFile,
    pdfPreview,
    previewingIds,
  } = usePdfPreview();

  const {
    changedCells,
    changedCellsRef,
    markCellChanged,
    resetChangedCells,
    setChangedCells,
  } = useChangedCells();

  const {
    documentVersions,
    handleCloseVersionsModal,
    handleDownloadVersion,
    handleOpenVersionsModal,
    selectedDocument,
    versionsLoading,
    versionsModalVisible,
  } = useDocumentVersions();

  const additionalFields = useSelector(
    (state: RootState) => state.companyAdditionalFields.additionalFields,
  );

  // ─── Stable Infinite Row Model datasource ────────────────────────────────
  const datasource = useRef<any>({
    getRows: async (params: any) => {
      const { startRow, endRow } = params;
      setLoading(true);

      const apiParams = new Map<string, any>();
      apiParams.set("company_id", companyIdRef.current);
      const bp = backendParamsRef.current;
      if (bp.ordering) apiParams.set("ordering", bp.ordering);
      if (bp.filter_specs) apiParams.set("filter_specs", bp.filter_specs);

      try {
        const localRows = documentsRef.current.filter(
          (doc) => doc.id !== undefined && doc.id < 0,
        );
        const localRowsInBlock = localRows.slice(startRow, endRow);
        const remainingRows = endRow - startRow - localRowsInBlock.length;
        const backendStartRow = Math.max(startRow - localRows.length, 0);
        const result = await getList<Document>(
          "/documents/",
          remainingRows,
          backendStartRow,
          apiParams,
        );

        if (result.status === SUCCESS) {
          const items = remainingRows > 0 ? result.data!.items || [] : [];
          const total = result.data!.count ?? 0;
          const enriched = items.map((doc) => ({
            ...doc,
            typeName:
              assetsRef.current.find((a) => a.id === doc.assetId)?.typeName ||
              "",
          }));
          // successCallback(rows, lastRow) — lastRow is the total count
          params.successCallback(
            [...localRowsInBlock, ...enriched],
            total + localRows.length,
          );
          resetChangedCells();
        } else {
          params.failCallback();
          warningAlert(result.detail || "Error desconocido");
        }
      } catch {
        params.failCallback();
        warningAlert("Error al cargar documentos");
      } finally {
        setLoading(false);
      }
    },
  });

  // Register datasource once grid and company are both ready
  const datasourceSet = useRef(false);
  useEffect(() => {
    if (gridApi && companyId !== undefined && !datasourceSet.current) {
      gridApi.setGridOption("datasource", datasource.current);
      datasourceSet.current = true;
    }
  }, [gridApi, companyId]);

  // Re-fetch from page 1 when filters / sort change
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (gridApi && datasourceSet.current) {
      gridApi.setGridOption("datasource", datasource.current);
    }
  }, [backendParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId !== undefined) fetchReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ─── Grid callbacks ───────────────────────────────────────────────────────
  const onGridReady = (params: any) => setGridApi(params.api);

  const onGridSortChanged = (event: any) => {
    const sortedCol = (event.api.getColumnState() as any[]).find(
      (s) => s.sort != null,
    );
    const ordering = sortedCol
      ? SORT_MAP[sortedCol.colId]?.[sortedCol.sort]
      : undefined;
    setBackendParams((prev) => ({ ...prev, ordering }));
  };

  const onGridFilterChanged = (event: any) => {
    const model = event.api.getFilterModel() as Record<string, any>;

    const RELATIONSHIP_LOOKUP: Record<
      string,
      { items: Array<{ id?: number; name?: string }> }
    > = {
      subcompanyId: { items: subcompanies },
      assetId: { items: assets },
      documentCategoryId: { items: documentCategories },
      documentSubcategoryId: { items: documentSubcategories },
    };

    const matchText = (text: string, value: string, type: string): boolean => {
      const t = text.toLowerCase(),
        v = value.toLowerCase();
      if (type === "equals") return t === v;
      if (type === "notEqual") return t !== v;
      if (type === "startsWith") return t.startsWith(v);
      if (type === "endsWith") return t.endsWith(v);
      if (type === "notContains") return !t.includes(v);
      return t.includes(v);
    };

    const specs: Array<{ field: string; value: string; type: string }> = [];

    for (const [colId, def] of Object.entries(model)) {
      const { filter: filterValue, type: filterType = "contains" } = def as any;
      if (colId in RELATIONSHIP_LOOKUP) {
        if (filterValue) {
          const ids = RELATIONSHIP_LOOKUP[colId].items
            .filter((item) => matchText(item.name!, filterValue, filterType))
            .map((item) => item.id);
          specs.push({
            field: colId,
            value: ids.length > 0 ? ids.join(",") : "-1",
            type: "in",
          });
        }
      } else if (filterType === "blank" || filterType === "notBlank") {
        specs.push({ field: colId, value: "", type: filterType });
      } else if (filterValue != null && filterValue !== "") {
        specs.push({
          field: colId,
          value: String(filterValue),
          type: filterType,
        });
      }
    }

    setBackendParams((prev) => ({
      ...prev,
      filter_specs: specs.length > 0 ? JSON.stringify(specs) : undefined,
    }));
  };

  // fetchDocuments for hooks that need to trigger a reload
  const fetchDocuments = async () => {
    if (gridApi) gridApi.setGridOption("datasource", datasource.current);
  };

  // ─── Row actions / persistence ────────────────────────────────────────────
  const { addRowLoading, handleAddNewRow } = useDocumentRowActions({
    additionalFields,
    assets,
    companyId: companyId ?? 0,
    documentCategories,
    documents,
    gridApi,
    setDocuments,
    subcompanies,
  });

  const { confirmDelete, deletingIds, handleFileUpload, handleSaveChanges } =
    useDocumentPersistence({
      additionalFields,
      changedCellsRef,
      companyId: companyId ?? 0,
      documents,
      fetchDocuments,
      gridApi,
      resetChangedCells,
      setDocuments,
      setLoading,
    });

  const onCellValueChanged = (event: any) =>
    markCellChanged(event.data.id, event.colDef.field);

  const getRowClass = (params: any) =>
    getSpreadsheetRowClass(params, changedCells);

  const changedCellCount = Object.values(changedCells).reduce(
    (acc, fields) => acc + fields.size,
    0,
  );

  const columns = useDocumentSpreadsheetColumns({
    additionalFields,
    changedCells,
    setChangedCells,
    subcompany: subcompanies,
    downloadingIds,
    previewingIds,
    documentCategoryId: documentCategories,
    documentSubcategoryId: documentSubcategories,
    assets,
    tags,
    deletingIds,
    setDocuments,
    setGridDateValue,
    markCellChanged,
    formatDateForInput,
    openNativeDatePicker,
    stopGridMouseEvent,
    isExpired,
    isPdfFile,
    handleDownloadClick,
    handlePreviewPdfClick,
    handleOpenVersionsModal,
    handleFileUpload,
    confirmDelete,
  });

  return (
    <CRow>
      <CCol lg="12">
        <CCard>
          <CCardHeader className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ fontWeight: "bold" }}>
                Cambios a aplicar: {changedCellCount}
              </div>
              <div className="d-flex gap-2">
                <CButton
                  type="button"
                  color="success"
                  onClick={() => handleAddNewRow("top")}
                  disabled={addRowLoading}
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i className="fa fa-plus" />
                  <span>Agregar Fila</span>
                  {addRowLoading && <span>(Cargando...)</span>}
                </CButton>
                <CButton
                  color="primary"
                  onClick={handleSaveChanges}
                  disabled={Object.keys(changedCells).length === 0}
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i className="fa fa-save" />
                  <span>Guardar</span>
                </CButton>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <DocumentSpreadsheetGrid
              columns={columns}
              loading={loading}
              getRowClass={getRowClass}
              onCellValueChanged={onCellValueChanged}
              onGridReady={onGridReady}
              onSortChanged={onGridSortChanged}
              onFilterChanged={onGridFilterChanged}
            />
          </CCardBody>
        </CCard>
      </CCol>
      <PdfPreviewModal preview={pdfPreview} onClose={closePdfPreview} />
      <VersionsModal
        show={versionsModalVisible}
        selectedDocument={selectedDocument}
        documentVersions={documentVersions}
        versionsLoading={versionsLoading}
        onClose={handleCloseVersionsModal}
        onDownloadVersion={handleDownloadVersion}
      />
    </CRow>
  );
};

export default DocumentSpreadsheetCrud;
